import { Injectable } from '@angular/core';
import { CurrentInfoHolder } from '../holders/current-info-holder';
import { WorkWeekService } from '../services/work-week.service';
import { LocaleService } from './locale.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentInfoService {
  _currentInfo: CurrentInfoHolder = new CurrentInfoHolder();

  constructor(
    private workweekservice: WorkWeekService,
    private localeservice: LocaleService
  ) {}

  public Initialize(currentInfo: any) {
    if (currentInfo === null || currentInfo === undefined)
      throw new Error('currentInfo is null or undefined');

    this._currentInfo = new CurrentInfoHolder();

    this._currentInfo = Object.assign(this._currentInfo, currentInfo);
  }

  public getOrderTypes(): any {
    return {
      Balcony: this._currentInfo.Balcony,
      BalconyWaitTime: this.getWaitTimeString(1),
      Delivery: this._currentInfo.Delivery,
      DeliveryWaitTime: this.getWaitTimeString(2),
      HasFee: this.hasFee(),
      FeeString: this.getFeeString(),
      HasFloor: this.hasFloor(),
      FloorValueString: this.getFloorValueString(),
      WillComeHere: this._currentInfo.WillComeHere,
      WillComeHereWaitTime: this.getWaitTimeString(3),
      Drive: this._currentInfo.Drive,
      DriveWaitTime: this.getWaitTimeString(4),
    };
  }

  getWaitTime(type: number): number {
    if (!this.workweekservice.isOpen()) return 0;

    switch (type) {
      case 0:
        return Number(this._currentInfo.TableWaitTime);
      case 1:
        return Number(this._currentInfo.BalconyWaitTime);
      case 2:
        return Number(this._currentInfo.DeliveryWaitTime);
      case 3:
        return Number(this._currentInfo.WillComeHereWaitTime);
      case 4:
        return Number(this._currentInfo.DriveWaitTime);
      default:
        return 0;
    }
  }

  getTagWaitTime(tags: Array<string>) {
    var maxWaitTime = 0;

    if (!this.workweekservice.isOpen()) return maxWaitTime;

    for (const key in this._currentInfo.TagWaitTime) {
      if (
        Object.prototype.hasOwnProperty.call(this._currentInfo.TagWaitTime, key)
      ) {
        const element = this._currentInfo.TagWaitTime[key];

        var tagWaitTime = tags.find((t) => t === element.Tag);

        if (tagWaitTime !== undefined) {
          var waitTime = Number(element.WaitTime);

          if (waitTime > maxWaitTime) maxWaitTime = waitTime;
        }
      }
    }

    return maxWaitTime;
  }

  getMaxTagWaitTime() {
    var maxWaitTime = 0;

    if (!this.workweekservice.isOpen()) return maxWaitTime;

    for (const key in this._currentInfo.TagWaitTime) {
      if (
        Object.prototype.hasOwnProperty.call(this._currentInfo.TagWaitTime, key)
      ) {
        const element = this._currentInfo.TagWaitTime[key];

        var waitTime = Number(element.WaitTime);

        if (waitTime > maxWaitTime) maxWaitTime = waitTime;
      }
    }

    return maxWaitTime;
  }

  getMinutes(waitTime: number) {
    return Math.round(waitTime / 600000000);
  }

  getWaitTimeString(type: number): string {
    if (!this.workweekservice.isOpen()) return '';

    let mins = this.getMinutes(this.getWaitTime(type));
    let tagMins = this.getMinutes(this.getMaxTagWaitTime());

    if (mins + tagMins > 0) {
      var minimum = mins;
      var maximum = mins + tagMins;

      if (minimum === maximum) {
        if (mins < 60) {
          return '(' + mins.toString() + 'min)';
        } else {
          var hours = mins / 60;

          return '(' + hours.toString() + 'h)';
        }
      } else {
        var minimumString = '';
        var maximumString = '';

        if (minimum < 60) minimumString = minimum.toString() + 'min';
        else {
          var hours = minimum / 60;

          minimumString = hours.toString() + 'h';
        }

        if (maximum < 60) maximumString = maximum.toString() + 'min';
        else {
          var hours = maximum / 60;

          maximumString = hours.toString() + 'h';
        }

        return '(' + minimumString + ' - ' + maximumString + ')';
      }
    }

    return '?';
  }

  public isOpen(): boolean {
    return this._currentInfo.Open === true;
  }

  public getClosedMessage() {
    if (
      this._currentInfo.NotOpenMessage !== null &&
      this._currentInfo.NotOpenMessage !== undefined
    )
      return String(this._currentInfo.NotOpenMessage);

    return this.localeservice.getTranslation('Pages.Storehome.Closed.Message');
  }

  public hasFee() {
    return (
      this._currentInfo.ChargeDeliveryFee === true ||
      this._currentInfo.ChargeByDistance ||
      this._currentInfo.ChargeByRegion ||
      this._currentInfo.ChargeByValue
    );
  }

  public getFee() {
    return this._currentInfo.DeliveryFee;
  }

  getDistanceFeeMinMax() {
    var maxValue =
      this._currentInfo.InitialFee +
      (this._currentInfo.MaximumDistance - this._currentInfo.InitialDistance) *
        this._currentInfo.FeeByDistanceUnit;

    return { min: this._currentInfo.InitialFee, max: maxValue };
  }

  getRegionFeeMinMax() {
    var sortedArray = this._currentInfo.DeliveryRegions.sort((x) => x.Fee);

    if (sortedArray.length === 1)
      return { min: sortedArray[0].Fee, max: sortedArray[0].Fee };

    if (sortedArray.length > 1)
      return {
        min: sortedArray[0].Fee,
        max: sortedArray[sortedArray.length - 1].Fee,
      };

    return { min: 0, max: 0 };
  }

  public getFeeString() {
    if (this._currentInfo.ChargeDeliveryFee === true)
      return `${this.localeservice.instantTranslation(
        'Pages.OrderType.Delivery.FeeString.DeliveryFee'
      )} ${this.localeservice.getCurrencyString(this.getFee())}`;

    if (this._currentInfo.ChargeByDistance) {
      var distanceMinMax = this.getDistanceFeeMinMax();

      if (this._currentInfo.DeliveryFee > 0)
        return `${this.localeservice.instantTranslation(
          'Pages.OrderType.Delivery.FeeString.StartAt'
        )} ${this.localeservice.getCurrencyString(distanceMinMax.min)}`;
      else
        return `${this.localeservice.instantTranslation(
          'Pages.OrderType.Delivery.FeeString.NoTaxUpTo'
        )} ${
          this._currentInfo.InitialDistance
        }${this.localeservice.instantTranslation(
          'Pages.OrderType.Delivery.FeeString.DistanceUnit'
        )}`;
    }

    if (this._currentInfo.ChargeByRegion) {
      var distanceMinMax = this.getRegionFeeMinMax();

      var freeRegions = this._currentInfo.DeliveryRegions.filter(
        (x) => x.Fee === 0
      );

      if (freeRegions.length === 0)
        return `${this.localeservice.instantTranslation(
          'Pages.OrderType.Delivery.FeeString.StartAt'
        )} ${this.localeservice.getCurrencyString(distanceMinMax.min)}`;
      else {
        const maxFreeDistance = Math.max(
          ...freeRegions.map((o) => o.EndDistance),
          0
        );

        return `${this.localeservice.instantTranslation(
          'Pages.OrderType.Delivery.FeeString.NoTaxUpTo'
        )} ${maxFreeDistance}${this.localeservice.instantTranslation(
          'Pages.OrderType.Delivery.FeeString.DistanceUnit'
        )}`;
      }
    }

    if (this._currentInfo.ChargeByValue)
      return `Taxa de ${this.localeservice.getCurrencyString(
        this._currentInfo.ByValueFee
      )} (Sem taxa para pedidos acima de ${this.localeservice.getCurrencyString(
        this._currentInfo.ValueThreshold
      )})`;

    return '?';
  }

  public GetDeliveryRegion(distance: number) {
    for (
      let index = 0;
      index < this._currentInfo.DeliveryRegions.length;
      index++
    ) {
      const element = this._currentInfo.DeliveryRegions[index];

      if (distance >= element.StartDistance && distance < element.EndDistance)
        return element;
    }

    return undefined;
  }

  public getMaxDistance() {
    if (this._currentInfo.ChargeByRegion === true) {
      var maxDistance = 0;

      for (
        let index = 0;
        index < this._currentInfo.DeliveryRegions.length;
        index++
      ) {
        const element = this._currentInfo.DeliveryRegions[index];

        if (element.EndDistance > maxDistance)
          maxDistance = element.EndDistance;
      }

      return maxDistance;
    }

    return Number(this._currentInfo.MaximumDistance);
  }

  getRegions() {
    return this._currentInfo.DeliveryRegions;
  }

  isWithinOperationArea(distance: number) {
    if (this._currentInfo.ChargeByRegion === true)
      return this.GetDeliveryRegion(distance) != undefined;

    return this._currentInfo.MaximumDistance >= distance;
  }

  public hasFloor() {
    return this._currentInfo.OrderFloor === true;
  }

  public getFloorValue() {
    return this._currentInfo.FloorValue;
  }

  public getFloorValueString() {
    return this.localeservice.getCurrencyString(this.getFloorValue());
  }
}
